import React, { useEffect, useState } from "react";
import { Grid, Typography, Box, Button, Card, CardContent, CardHeader, Divider, LinearProgress, useTheme, CircularProgress, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import ShoppingBag from "@mui/icons-material/ShoppingBag";
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { MedicalPackageDashboardDTO } from "../../../../api/DashboardWidgetDTO";
import { DashboardWidgetsClient } from "../../../../api/DashboardWidgetsClient";
import { AVANGARD_WEB_URL, REACT_APP_PORTAL_API_URL } from "../../../../KeyVariables";
import { PatientWidgetProps } from "../PatientDashboard";
import Scrollbars from "react-custom-scrollbars-2";

export const MedicalPackageWidget = ({ patientId }: PatientWidgetProps) => {
  const { t } = useTranslation("Portal");
  const [packages, setPackages] = useState<MedicalPackageDashboardDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await new DashboardWidgetsClient(REACT_APP_PORTAL_API_URL).packages(patientId > 0 ? patientId : undefined);
      setPackages(data);
    } catch (error) {
      console.error("Error fetching appointment data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <ShoppingBag fontSize="medium" sx={{ mr: 1 }} />
            <Typography variant={isMobile ? "h5" : "h6" } fontWeight="bold">
              {t("Portal_MedicalPackageList")}
            </Typography>
            {isLoading && <CircularProgress sx={{ml:1}} size={20}/>}
          </Box>
        }
        action={
          <Button variant="contained" size="small" onClick={() => window.open(`${AVANGARD_WEB_URL}/${i18n.language.slice(0, 2)}/promotions`,'_self')}>
            {t("Portal_MedicalPackageListMore")}
          </Button>
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          {packages?.length > 0 ? (
            packages.map((pkg, index) => {
              const totalServices = pkg.services?.length || 0;
              const notApplied = pkg.services?.filter(service => !service.isApplied).length || 0;
              const applied = pkg.services?.filter(service => service.isApplied).length || 0;
              const allApplied = applied == totalServices;
              const progress = (notApplied / totalServices) * 100;
              const theme = useTheme();

              return (
                
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      p: 1,
                      border: "2px solid #ddd",
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{mb:0.5}}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          backgroundColor: theme.colors.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold" color="white">
                          {pkg.name!.split(" ").map(word => word[0]).join("").toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ textDecoration: allApplied ? "line-through" : "inherit" }}>
                        {pkg.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {applied}/{totalServices}
                      </Typography>
                    </Box>

                    <Box sx={{ width: "100%", mb: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 5,
                          backgroundColor: "#4caf50",
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: "#00A3F4"
                          }
                        }}
                      />
                    </Box>
                    <Scrollbars autoHeight autoHide autoHeightMax={isMobile ? '30vh' : "40vh"}>
                    {pkg.services?.map((service, serviceIndex) => (
                        <Box display="flex" alignItems="center" key={serviceIndex} mb={0.5}>
                          {service.isApplied ? (
                            <CheckIcon sx={{ color: "#4caf50", mr: 1 }} />
                          ) : (
                            <HourglassEmptyIcon sx={{ color: "#00A3F4", mr: 1 }} />
                          )}
                          <Typography variant="body2">{service.name}</Typography>
                        </Box>
                    ))}
                    </Scrollbars>
                  </Box>
                </Grid>

              );
            })
          ) : (
            <Typography px={2} pt={2} variant="h6">
              Nu aveți pachete activate. Accesați "Mai multe" pentru a vedea ofertele disponibile.
            </Typography>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
